/**
 * Challenges Screen — 2 main tabs: Shaxsiy (personal) + Ommaviy (public).
 * Ommaviy has sub-tabs: Hammasi, Turnifly, Ekspertlar, Tanlovlar.
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Trophy, Sparkles, Crown, Search, User, Globe, Gem,
} from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useChallengeStore } from '@turnifly/shared/stores'
import { useUserChallenges, useLeaveChallenge } from '@turnifly/shared/hooks'
import { ChallengeCreateSheet } from './challenge-create-sheet'
import { TypeSelectionSheet } from './type-selection-sheet'
import { PersonalTab } from './personal-tab'
import { AllTab } from './all-tab'
import { TurniflyTab } from './turnifly-tab'
import { ExpertsTab } from './experts-tab'
import { CompetitionsTab } from './competitions-tab'
import { SearchOverlay } from './search-overlay'
import type { UserChallengeItem } from '@turnifly/shared/api'

type MainTab = 'personal' | 'public'
type PublicSubTab = 'all' | 'turnifly' | 'experts' | 'competitions'

const PUBLIC_SUB_TABS: Array<{ id: PublicSubTab; icon: React.ReactNode }> = [
  { id: 'all', icon: <Globe size={12} /> },
  { id: 'turnifly', icon: <Sparkles size={12} /> },
  { id: 'experts', icon: <Crown size={12} /> },
  { id: 'competitions', icon: <Trophy size={12} /> },
]

export function ChallengesScreen(): React.ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const userId = user?.id ?? ''
  const { setCreateOpen, setSelectedType } = useChallengeStore()
  const { data: raw, isLoading } = useUserChallenges(userId)
  const leaveMutation = useLeaveChallenge()

  const [mainTab, setMainTab] = useState<MainTab>('personal')
  const [publicSubTab, setPublicSubTab] = useState<PublicSubTab>('all')
  const [activeOpen, setActiveOpen] = useState(true)
  const [completedOpen, setCompletedOpen] = useState(false)
  const [createdOpen, setCreatedOpen] = useState(false)
  const [typeSheetOpen, setTypeSheetOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const challenges = useMemo(() => {
    if (!raw) return { active: [], completed: [], created: [] }
    const items = raw as UserChallengeItem[]
    return {
      active: items.filter((c) => c.status === 'active' || c.challenge.status === 'upcoming'),
      completed: items.filter((c) => c.status === 'completed' || c.challenge.status === 'completed'),
      created: items.filter((c) => c.challenge.creator_id === userId),
    }
  }, [raw, userId])

  const isPremium = user?.is_premium ?? false
  const isExpert = user?.badge_type === 'expert'
  const isCompany = user?.badge_type === 'organization' || user?.badge_type === 'company'
  const canCreateAny = isExpert || isCompany

  const handleFabPress = useCallback((): void => {
    if (!isPremium) { navigate('/profile/premium'); return }
    setTypeSheetOpen(true)
  }, [isPremium, navigate])

  const handleTypeSelect = useCallback((type: 'private' | 'public' | 'competition'): void => {
    if (!isPremium) return
    if (type !== 'private' && !canCreateAny) return
    setTypeSheetOpen(false)
    setSelectedType(type)
    setCreateOpen(true)
  }, [isPremium, canCreateAny, setSelectedType, setCreateOpen])

  return (
    <div className="min-h-screen pb-28">
      {/* Header */}
      <div className="sticky top-14 z-20 bg-[var(--color-bg)]/90 backdrop-blur-sm border-b border-white/[0.04]">
        <div className="max-w-[600px] mx-auto px-4 pt-3 pb-2">
          {/* Title + search */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy size={20} className="text-primary" />
              <h1 className="text-[17px] font-display font-bold text-text">
                {t('tabs.challenges')}
              </h1>
            </div>
            <button
              onClick={() => setSearchOpen(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/[0.04] text-text-hint hover:bg-white/[0.06] transition-colors tap-highlight-transparent"
            >
              <Search size={16} />
            </button>
          </div>

          {/* Main tabs: Shaxsiy / Ommaviy */}
          <div className="flex gap-1 mb-2">
            {(['personal', 'public'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setMainTab(tab)}
                className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[13px] font-semibold transition-colors tap-highlight-transparent ${
                  mainTab === tab ? 'text-white' : 'text-text-hint hover:bg-white/[0.04]'
                }`}
              >
                {mainTab === tab && (
                  <motion.div
                    layoutId="mainTabBg"
                    className="absolute inset-0 rounded-xl bg-primary"
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-1.5">
                  {tab === 'personal' ? <User size={14} /> : <Globe size={14} />}
                  {t(`challenges.tab.${tab}`)}
                </span>
              </button>
            ))}
          </div>

          {/* Public sub-tabs */}
          {mainTab === 'public' && (
            <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {PUBLIC_SUB_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setPublicSubTab(tab.id)}
                  className={`relative shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors tap-highlight-transparent ${
                    publicSubTab === tab.id
                      ? 'text-primary bg-primary/10'
                      : 'text-text-hint hover:bg-white/[0.04]'
                  }`}
                >
                  {tab.icon}
                  {t(`challenges.tab.${tab.id}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-[600px] mx-auto px-4 pt-3">
        {mainTab === 'personal' && (
          <PersonalTab
            challenges={challenges}
            isLoading={isLoading}
            userId={userId}
            activeOpen={activeOpen}
            completedOpen={completedOpen}
            createdOpen={createdOpen}
            setActiveOpen={setActiveOpen}
            setCompletedOpen={setCompletedOpen}
            setCreatedOpen={setCreatedOpen}
            leaveMutation={leaveMutation}
          />
        )}
        {mainTab === 'public' && publicSubTab === 'all' && <AllTab />}
        {mainTab === 'public' && publicSubTab === 'turnifly' && <TurniflyTab />}
        {mainTab === 'public' && publicSubTab === 'experts' && <ExpertsTab />}
        {mainTab === 'public' && publicSubTab === 'competitions' && <CompetitionsTab />}
      </div>

      {/* FAB */}
      <div className="fixed bottom-[calc(var(--tg-safe-area-inset-bottom)+80px)] right-4 z-40">
        <button
          onClick={handleFabPress}
          className="relative w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-primary/30 hover:scale-105 transition-transform active:scale-95 tap-highlight-transparent"
        >
          <Plus size={24} strokeWidth={2.5} />
          {!isPremium && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow-sm">
              <Gem size={10} className="text-white" />
            </span>
          )}
        </button>
      </div>

      <TypeSelectionSheet
        isOpen={typeSheetOpen}
        onClose={() => setTypeSheetOpen(false)}
        onSelect={handleTypeSelect}
        isPremium={isPremium}
        canCreateAny={canCreateAny}
        navigate={navigate}
      />
      <ChallengeCreateSheet />
      <AnimatePresence>
        <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      </AnimatePresence>
    </div>
  )
}
